import React, { useEffect, useState } from 'react';
import BasePage from 'pages/common/basePage';
import Header from 'pages/common/header';
import MainContent from 'pages/common/mainContent';
import { ShortcutsHeaderButtons } from './components/shortcutsHeaderButtons';
import ShortcutsListing from './components/shortcutsListing';
import CreateShortcutGroupDialog from './components/dialogs/createShortcutGroupDialog';
import CreateShortcutDialog from './components/dialogs/createShortcutDialog';
import { ShortcutGroup, ShortcutGroupToShortcuts, ShortcutUpdateModelv1, ShortcutsFilterv1, defaultShortcutsFilter } from './models';
import { createShortcut, createShortcutGroup, getOriginalShortcutFormat, getShortcutFormat, SET_ERR_MSG, SET_SUCCESS_MSG, updateShortcutsFormat } from './reducer';
import { getAudiences } from 'modules/audiences/actionCreator';
import { Button, Paper } from '@mui/material';
import ShortcutsFormatEditor from './components/editor/shortcutsFormatEditor';
import ShortcutsOrganizeListHeaderButtons from './components/editor/shortcutsOrganizeListHeaderButtons';
import ShallowEquals from 'utils/shallowEquals';
import { useDispatch, useSelector } from 'react-redux';
import { GlobalApplicationState } from 'globalApplicationState';
import { useSetSnackbarMessages } from 'modules/common/hooks/useSetSnackbarMessages';

import "./styles/shortcutsManagementPage.sass";

interface IShortcutsManagementPageProps {}

export const ShortcutsManagementPage: React.FC<IShortcutsManagementPageProps> = () => {
    const [isReorganizeListMode, setIsReorganizeListMode] = useState<boolean>(false);
    const [showCreateShortcutDialog, setShowCreateShortcutDialog] = useState(false);
    const [shortcutsFilter, setShortcutsFilter] = React.useState<ShortcutsFilterv1>(defaultShortcutsFilter);
    const [showCreateShortcutGroupDialog, setShowCreateShortcutGroupDialog] = useState(false);
    const { filteredShortcutFormat, successMessage, errorMessage } = useSelector((state: GlobalApplicationState) => state.shortcuts);
    const [newShortcutFormat, setNewShortcutFormat] = useState<ShortcutGroupToShortcuts[]>(filteredShortcutFormat);
    const audiences = useSelector((state: GlobalApplicationState) => state.audiences.audiences);
    const [expandedShortcutGroups, setExpandedShortcutGroups] = React.useState<string[]>(filteredShortcutFormat.map(f => f.shortcutGroup.id));
    const [selectedShortcutGroup, setSelectedShortcutGroup] = useState<ShortcutGroup | undefined>(undefined);
    const collapseMsg = "Collapse All";
    const expandMsg = "Expand All";
    const isAllExpanded = expandedShortcutGroups.length === filteredShortcutFormat.map(f => f.shortcutGroup).length && expandedShortcutGroups.length !== 0;
    const dispatch = useDispatch();
    const { setSuccessMessage, setErrorMessage } = useSetSnackbarMessages();

    const fetchShortcutFormat = (filter?: ShortcutsFilterv1) => {
        dispatch(getShortcutFormat(filter ?? shortcutsFilter));
    }

    useEffect(() => {
        if (successMessage) {
            setSuccessMessage(successMessage);
            dispatch(SET_SUCCESS_MSG(""));
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) {
            setErrorMessage(errorMessage);
            dispatch(SET_ERR_MSG(""));
        }
    }, [errorMessage]);

    useEffect(() => {
        setNewShortcutFormat(filteredShortcutFormat);
        setExpandedShortcutGroups(filteredShortcutFormat.map(f => f.shortcutGroup.id));
    }, [filteredShortcutFormat]);

    useEffect(() => {
        fetchShortcutFormat();
        dispatch(getOriginalShortcutFormat());
        dispatch(getAudiences());
    }, []);

    const onFilterChange = async (filter: ShortcutsFilterv1) => {
        if (!ShallowEquals(filter, shortcutsFilter)) {
            setShortcutsFilter(filter);
        }

        fetchShortcutFormat(filter);
    }

    const onCreateShortcutGroupFromInput = async (groupName: string) => {
        setShowCreateShortcutGroupDialog(false);
        dispatch(createShortcutGroup({
            shortcutGroupName: groupName, 
            filter: shortcutsFilter, 
            scrollToId: filteredShortcutFormat[filteredShortcutFormat.length - 1]?.shortcutGroup.id,
            successMsg: "Successfully added a new shortcut group."
        }));
    }

    const onCreateShortcutGroup = () => {
        setShowCreateShortcutGroupDialog(true);
    }

    const onCreateShortcutFromInput = async (input: ShortcutUpdateModelv1) => {
        setShowCreateShortcutDialog(false);
        const shortcutsOfShortcutGroup = filteredShortcutFormat.find(sgts => sgts.shortcutGroup.id === input.groupId)?.shortcuts;
        dispatch(createShortcut({
            shortcutUpdateModel: input, 
            filter: shortcutsFilter,
            scrollToId: shortcutsOfShortcutGroup && shortcutsOfShortcutGroup.length
                ? shortcutsOfShortcutGroup[shortcutsOfShortcutGroup.length - 1].id
                : input.groupId,
            successMsg: "Successfully added a new shortcut."
        }));
        setSelectedShortcutGroup(undefined);
    }

    const onCreateShortcut = (shortcutGroup?: ShortcutGroup) => {
        setSelectedShortcutGroup(shortcutGroup);
        setShowCreateShortcutDialog(true);
    }

    const onOrganizeList = () => {
        setIsReorganizeListMode(prev => !prev);
    }

    const onSaveNewShortcutFormat = async () => {
        setIsReorganizeListMode(false);
        dispatch(updateShortcutsFormat({
            newFormat: newShortcutFormat, 
            filter: shortcutsFilter, 
            successMsg: "Successfully saved changes."
        }));
    }

    return <BasePage fullWidth>
        <Header
            title={
                isReorganizeListMode
                    ? <>
                        <span className='shortcuts-page-title-fade'>Manage Shortcuts / </span> 
                        <span>&nbsp;Organize list</span>
                    </> 
                    : "Manage Shortcuts"
            }
            rightComponent={
                isReorganizeListMode 
                ? <ShortcutsOrganizeListHeaderButtons 
                    onCancel={() => {
                        setIsReorganizeListMode(false);
                        setNewShortcutFormat(filteredShortcutFormat);
                    }}
                    onSaveFormat={onSaveNewShortcutFormat}
                />
                : <ShortcutsHeaderButtons 
                    onCreateShortcutGroupClick={onCreateShortcutGroup} 
                    onCreateShortcutClick={onCreateShortcut}
                    onOrganizeListClick={onOrganizeList}
                />
            }
        />
        <MainContent>
            <div className="shortcuts-content-container">
                {
                    isReorganizeListMode
                        ? <ShortcutsFormatEditor
                            newShortcutFormat={newShortcutFormat}
                            setNewShortcutFormat={setNewShortcutFormat}
                        />
                        : <ShortcutsListing
                            onCreateShortcut={onCreateShortcut}
                            filteredShortcutFormat={filteredShortcutFormat}
                            audiences={audiences}
                            onFilterChange={onFilterChange}
                            expandedShortcutGroups={expandedShortcutGroups}
                            setExpandedShortcutGroups={setExpandedShortcutGroups}
                            shortcutsFilter={shortcutsFilter}
                        />
                }
                <CreateShortcutDialog 
                    show={showCreateShortcutDialog}
                    onClose={() => {
                        setSelectedShortcutGroup(undefined); 
                        setShowCreateShortcutDialog(false);
                    }}
                    onCreateShortcutFromInput={onCreateShortcutFromInput}
                    audiences={audiences}
                    selectedShortcutGroup={selectedShortcutGroup}
                />
                <CreateShortcutGroupDialog 
                    show={showCreateShortcutGroupDialog}
                    onClose={() => setShowCreateShortcutGroupDialog(false)}
                    onCreateShortcutGroupFromInput={onCreateShortcutGroupFromInput}
                />
            </div>
        </MainContent>
        <Paper className="shortcuts-content-footer">
            {   
                !isReorganizeListMode &&
                <Button 
                    onClick={_ => setExpandedShortcutGroups([...(isAllExpanded ? [] : filteredShortcutFormat.map(f => f.shortcutGroup.id))])}
                >
                    {isAllExpanded ? collapseMsg : expandMsg}
                </Button>
            }
        </Paper>
    </BasePage>;
};